import React from "react";
import PropTypes from "prop-types";
import AssetAllocationBars from "../AssetAllocationBars";
import { Table } from "components/Table";
import { Loader } from "components/Loader";
import {
  Container,
  HeaderContainer,
  TitleContainer,
  Title,
  Subtitle,
  Dropdown,
  StrategyTabsContainer,
  StrategyTab,
  StrategyIcon,
  StrategyLogo,
  Divider,
  ContentContainer,
  TreemapContainer,
  TreemapRectangle,
  RectangleName,
  // RectangleValue,
  RectanglePercentage,
  TableContainer,
  TableHeader,
  TableRow,
  TableRowLeft,
  ColorIndicator,
  IndustryName,
  PercentageBadge,
  AssetsContainer,
  AssetTableWrapper,
  AssetLogoContainer,
  AssetLogoImage,
  AssetLogoFallback,
  AssetCell,
  AssetInfo,
  AssetSymbol,
  AssetCompany,
  AssetText,
  AssetValueContainer,
  AssetValue,
  AssetShares,
  StrategyBadgesContainer,
  StrategyBadge,
  LoadingMessage,
  EmptyMessage,
} from "./StrategyOverviewTreemap.styles";
import { getStockCompanyIcon } from "utils/index";

const StrategyOverviewTreemap = ({
  dropdownValue = "By industry",
  onDropdownChange,
  industries = [],
  assets = [],
  strategies = [
    { name: "Strategy A", iconUrl: null, logo: "A" },
    { name: "Strategy B", iconUrl: null, logo: "B" },
    { name: "Strategy C", iconUrl: null, logo: "C" },
  ],
  activeStrategyIndex = 0,
  onStrategyChange,
  containerStyle = {},
  treemapStyle = {},
  tableStyle = {},
  isLoadingAssets = false,
}) => {
  const hexToRgb = (hex) => {
    if (!hex) return { r: 0, g: 0, b: 0 };
    const normalized = hex.replace("#", "");
    const bigint = parseInt(
      normalized.length === 3
        ? normalized
            .split("")
            .map((c) => c + c)
            .join("")
        : normalized,
      16
    );
    const r = (bigint >> 16) & 255;
    const g = (bigint >> 8) & 255;
    const b = bigint & 255;
    return { r, g, b };
  };

  const isLightColor = (color) => {
    const { r, g, b } = hexToRgb(color);
    // YIQ perceived brightness
    const yiq = (r * 299 + g * 587 + b * 114) / 1000;
    return yiq >= 180; // threshold for light backgrounds
  };

  const getTextColor = (backgroundColor) => {
    return isLightColor(backgroundColor) ? "#0f172a" : "#ffffff";
  };

  const getTextOpacity = (backgroundColor) => {
    return isLightColor(backgroundColor) ? 1 : 0.9;
  };

  const needsBorder = (backgroundColor) => {
    return isLightColor(backgroundColor);
  };

  const renderIndustryView = () => {
    // Squarified treemap algorithm for better visual layout
    const createTreemapLayout = (items, width, height) => {
      const sortedItems = [...items]
        .map((item) => ({ ...item, value: parseFloat(item.percentage) }))
        .sort((a, b) => b.value - a.value);

      const rectangles = [];

      const squarify = (data, x, y, w, h) => {
        if (data.length === 0) return;

        if (data.length === 1) {
          rectangles.push({
            ...data[0],
            x,
            y,
            width: w,
            height: h,
          });
          return;
        }

        const total = data.reduce((sum, d) => sum + d.value, 0);
        const item = data[0];
        const itemRatio = item.value / total;

        // Decide whether to split horizontally or vertically
        const horizontal = w > h;

        if (horizontal) {
          const itemWidth = w * itemRatio;
          rectangles.push({
            ...item,
            x,
            y,
            width: itemWidth,
            height: h,
          });
          squarify(data.slice(1), x + itemWidth, y, w - itemWidth, h);
        } else {
          const itemHeight = h * itemRatio;
          rectangles.push({
            ...item,
            x,
            y,
            width: w,
            height: itemHeight,
          });
          squarify(data.slice(1), x, y + itemHeight, w, h - itemHeight);
        }
      };

      squarify(sortedItems, 0, 0, width, height);
      return rectangles;
    };

    const isMobile = window.innerWidth < 768;
    const containerWidth = isMobile ? 300 : 600;
    const containerHeight = isMobile ? 300 : 420;
    const rectangles = createTreemapLayout(
      industries,
      containerWidth,
      containerHeight
    );

    return (
      <>
        <TreemapContainer style={treemapStyle}>
          {rectangles.map((rect) => (
            <TreemapRectangle
              key={rect.name}
              $left={(rect.x / containerWidth) * 100}
              $top={(rect.y / containerHeight) * 100}
              $width={(rect.width / containerWidth) * 100}
              $height={(rect.height / containerHeight) * 100}
              $color={rect.color}
              $textColor={getTextColor(rect.color)}
            >
              <RectangleName
                $fontSize={
                  isMobile
                    ? rect.width > 60
                      ? "10px"
                      : rect.width > 40
                      ? "9px"
                      : "8px"
                    : rect.width > 80
                    ? "11px"
                    : rect.width > 50
                    ? "10px"
                    : "9px"
                }
              >
                {rect.width < (isMobile ? 40 : 50)
                  ? rect.name.split(" ")[0]
                  : rect.name}
              </RectangleName>
              {/* {rect.width > (isMobile ? 35 : 45) && (
                <RectangleValue
                  $fontSize={
                    isMobile
                      ? rect.width > 50
                        ? "9px"
                        : "8px"
                      : rect.width > 70
                      ? "10px"
                      : "9px"
                  }
                  $textColor={getTextColor(rect.color)}
                  $opacity={getTextOpacity(rect.color)}
                >
                  {rect.value}
                </RectangleValue>
              )} */}
              <RectanglePercentage
                $fontSize={
                  isMobile
                    ? rect.width > 40
                      ? "8px"
                      : "7px"
                    : rect.width > 60
                    ? "10px"
                    : "9px"
                }
                $textColor={getTextColor(rect.color)}
                $opacity={getTextOpacity(rect.color)}
                title={`${rect.name}: ${rect.percentage}`}
              >
                {rect.percentage}
              </RectanglePercentage>
            </TreemapRectangle>
          ))}
        </TreemapContainer>

        <TableContainer style={tableStyle}>
          <TableHeader>
            <span>Industry</span>
            <span>Percentage</span>
          </TableHeader>
          {industries.map((industry, index) => (
            <TableRow
              key={industry.name}
              $hasBorder={index < industries.length - 1}
            >
              <TableRowLeft>
                <ColorIndicator
                  $color={industry.color}
                  $needsBorder={needsBorder(industry.color)}
                />
                <IndustryName>{industry.name}</IndustryName>
              </TableRowLeft>
              <PercentageBadge>{industry.percentage}</PercentageBadge>
            </TableRow>
          ))}
        </TableContainer>
      </>
    );
  };

  const renderAssetsView = () => {
    if (isLoadingAssets) {
      return (
        <AssetsContainer>
          <LoadingMessage>
            <Loader />
          </LoadingMessage>
        </AssetsContainer>
      );
    }

    if (!assets || assets.length === 0) {
      return (
        <AssetsContainer>
          <EmptyMessage>
            No assets data available. Please adjust your allocation settings.
          </EmptyMessage>
        </AssetsContainer>
      );
    }

    const assetBarsData = assets.map((asset) => ({
      name: asset.symbol || asset.stock || asset.name || asset.id || "N/A",
      percentage: asset.percentage || asset.percent,
      color: asset.color,
    }));

    // Render asset logo with fallback
    const renderAssetLogo = (asset) => {
      const symbol = asset.symbol || asset.stock || asset.name || "A";
      const logoUrl =
        getStockCompanyIcon(symbol) ||
        `https://logo.clearbit.com/${symbol.toLowerCase()}.com`;

      return (
        <AssetLogoContainer>
          <AssetLogoImage
            src={logoUrl}
            alt={symbol}
            onError={(e) => {
              e.target.style.display = "none";
              const fallback = e.target.nextSibling;
              if (fallback) {
                fallback.style.display = "flex";
              }
            }}
          />
          <AssetLogoFallback>
            {symbol.charAt(0).toUpperCase()}
          </AssetLogoFallback>
        </AssetLogoContainer>
      );
    };

    // Define columns for the assets table
    const assetsColumns = [
      {
        dataField: "asset",
        text: "Assets",
        props: {
          cell: ({ row }) => {
            const asset = row.original;
            return (
              <AssetCell>
                {renderAssetLogo(asset)}
                <AssetInfo>
                  <AssetSymbol>
                    {asset.symbol || asset.stock || asset.name || "N/A"}
                  </AssetSymbol>
                  <AssetCompany>
                    {asset.company_name ||
                      asset.company ||
                      asset.symbol ||
                      asset.stock}
                  </AssetCompany>
                </AssetInfo>
              </AssetCell>
            );
          },
        },
      },
      {
        dataField: "weight",
        text: "Weight",
        props: {
          cell: ({ row }) => {
            const asset = row.original;
            return (
              <AssetText>
                {asset.weight || asset.percentage || asset.percent}
              </AssetText>
            );
          },
        },
      },
      {
        dataField: "sharePrice",
        text: "Share price",
        props: {
          cell: ({ row }) => {
            const asset = row.original;
            return <AssetText>{asset.sharePrice || asset.price}</AssetText>;
          },
        },
      },
      {
        dataField: "value",
        text: "Value",
        props: {
          cell: ({ row }) => {
            const asset = row.original;
            return (
              <AssetValueContainer>
                <AssetValue>{asset.value}</AssetValue>
                <AssetShares>{asset.shares} shares</AssetShares>
              </AssetValueContainer>
            );
          },
        },
      },
      {
        dataField: "fromStrategy",
        text: "From strategy",
        props: {
          cell: ({ row }) => {
            const asset = row.original;
            const strategies = asset.strategies || [
              asset.fromStrategy || asset.from_strategy,
            ];
            return (
              <StrategyBadgesContainer>
                {strategies.filter(Boolean).map((strategy, idx) => (
                  <StrategyBadge key={idx}>{strategy}</StrategyBadge>
                ))}
              </StrategyBadgesContainer>
            );
          },
        },
      },
    ];

    return (
      <>
        <AssetsContainer>
          <AssetAllocationBars assets={assetBarsData} />
          <AssetTableWrapper>
            <Table
              data={assets}
              columns={assetsColumns}
              isLoading={isLoadingAssets}
              showPagination={false}
              notFoundText="No assets available"
            />
          </AssetTableWrapper>
        </AssetsContainer>
      </>
    );
  };

  return (
    <Container style={containerStyle}>
      <HeaderContainer>
        <TitleContainer>
          <Title>Strategy Overview</Title>
          <Subtitle>We&apos;re generating your strategy overview...</Subtitle>
        </TitleContainer>

        {onDropdownChange && (
          <Dropdown value={dropdownValue} onChange={onDropdownChange}>
            <option value="By industry">By industry</option>
            <option value="By assets">By assets</option>
          </Dropdown>
        )}
      </HeaderContainer>

      <StrategyTabsContainer>
        {strategies.map((strategy, index) => {
          // Handle both string and object formats for backward compatibility
          const strategyName =
            typeof strategy === "string" ? strategy : strategy.name;
          const strategyIconUrl =
            typeof strategy === "object" ? strategy.iconUrl : null;
          const strategyLogo =
            typeof strategy === "object"
              ? strategy.logo
              : strategy.charAt(strategy.length - 1);
          const strategyId =
            typeof strategy === "object" ? strategy.id : strategyName;

          return (
            <StrategyTab
              key={strategyId || strategyName}
              $isActive={index === activeStrategyIndex}
              onClick={() => onStrategyChange && onStrategyChange(index)}
            >
              {strategyIconUrl ? (
                <StrategyIcon
                  src={strategyIconUrl}
                  alt={strategyName}
                  onError={(e) => {
                    // Fallback to letter logo if image fails
                    e.target.style.display = "none";
                    e.target.nextSibling.style.display = "flex";
                  }}
                />
              ) : null}
              <StrategyLogo
                style={{ display: strategyIconUrl ? "none" : "flex" }}
              >
                {strategyLogo}
              </StrategyLogo>
              {strategyName}
            </StrategyTab>
          );
        })}
      </StrategyTabsContainer>

      <Divider />

      <ContentContainer>
        {dropdownValue === "By assets"
          ? renderAssetsView()
          : renderIndustryView()}
      </ContentContainer>
    </Container>
  );
};

StrategyOverviewTreemap.propTypes = {
  industries: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      percentage: PropTypes.string.isRequired,
      color: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
    })
  ),

  assets: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      symbol: PropTypes.string,
      stock: PropTypes.string,
      company_name: PropTypes.string,
      company: PropTypes.string,
      percentage: PropTypes.string,
      percent: PropTypes.number,
      weight: PropTypes.string,
      color: PropTypes.string,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      price: PropTypes.number,
      sharePrice: PropTypes.string,
      shares: PropTypes.number,
      fromStrategy: PropTypes.string,
      from_strategy: PropTypes.string,
      strategies: PropTypes.arrayOf(PropTypes.string),
    })
  ),

  strategies: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        name: PropTypes.string.isRequired,
        iconUrl: PropTypes.string,
        logo: PropTypes.string,
      }),
    ])
  ),
  activeStrategyIndex: PropTypes.number,
  onStrategyChange: PropTypes.func,
  containerStyle: PropTypes.object,
  treemapStyle: PropTypes.object,
  tableStyle: PropTypes.object,
  dropdownValue: PropTypes.string,
  onDropdownChange: PropTypes.func,
  isLoadingAssets: PropTypes.bool,
};

export default StrategyOverviewTreemap;
